<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { useLogger } from 'vue-logger-plugin'
import { setStoreLogger } from '../logger.ts'
import { useCookies } from 'vue3-cookies'
import { ref, onMounted } from 'vue'


import { OrderDetailGet } from '../api/Order'
import { GetRootUrl, GetBeeNumberAuthenicatedUser } from '../stores/SettingsStore.ts'
import { trackPurchase, type IPurchaseData } from '../analytics/index'
import { buildMatomoItemsFromOrder } from '../analytics/matomoItemBuilder'
import { FormatCurrency } from '../services/currencyFormatter'
import { CustomerOrderGetList } from '../services/OrderService'

import type { IOrderDetailGetItem } from '../types/OrderTypes'

import { ClearCartFromLocalStorage } from '../services/CartService.ts'
import { GetFullProductImageDictionary } from '../services/SkuService.ts'
import { handleError } from '../services/ErrorService.ts'

import { Grid } from '@progress/kendo-vue-grid';
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { resolveColumnWidth } from '../utils/kendoHelper.ts'

const logger = useLogger()
setStoreLogger(logger)

const { cookies } = useCookies()
cookies.remove('AoeCartIdToken')
ClearCartFromLocalStorage()

const rootUrl = ref<string>('')
const viewModel = ref<IOrderDetailGetItem | null>(null)
const columns = [
        {
          field: 'productImage',
          title: t('thankYou.ProductImageHeader'),
          cell: 'orderItemGridImage',
          headerClassName: 'order-item-grid-image-header',
          width: resolveColumnWidth('120px', t('thankYou.ProductImageHeader'))
        },
        {
          field: 'productName',
          title: t('thankYou.DetailsHeader'),
          width: resolveColumnWidth('200px', t('thankYou.DetailsHeader'))
        },
        {
          field: 'price',
          title: t('thankYou.RetailHeader'),
          cell: 'orderItemGridPrice',
          width: resolveColumnWidth('90px', t('thankYou.RetailHeader')),
          className: 'text-end',
          headerClassName: 'text-end'
        },
        {
          field: 'totalDiscountAmount',
          title: t('thankYou.DiscountHeader'),
          cell: 'orderItemGridDiscount',
          width: resolveColumnWidth('90px', t('thankYou.DiscountHeader')),
          className: 'text-end',
          headerClassName: 'text-end'
        },
        {
          field: 'quantity',
          title: t('thankYou.QuantityHeader'),
          width: resolveColumnWidth('70px', t('thankYou.QuantityHeader')),
          className: 'text-center',
          headerClassName: 'order-item-grid-qty-header text-center',
        },
        {
          field: 'subtotal',
          title: t('thankYou.SubTotalHeader'),
          format: '{0:c}',
          cell: 'orderItemGridSubtotal',
          width: resolveColumnWidth('140px', t('thankYou.SubTotalHeader')),
          className: 'text-end',
          headerClassName: 'text-end'
        }
      ]

async function getPurchaseCustomerType(order: IOrderDetailGetItem): Promise<'new' | 'returning'> {
  // Only call CustomerOrderGetList if user is authenticated (API requires auth)
  const beeNumberAuth = await GetBeeNumberAuthenicatedUser()
  if (!beeNumberAuth) {
    // Not authenticated (guest/kit checkout) - assume new customer
    return 'new'
  }

  if (!order.beeNumberCustomer) {
    return 'returning'
  }

  const response = await CustomerOrderGetList()
  const orders = response?.items ?? []

  return orders.length > 1 ? 'returning' : 'new'
}

function getPurchaseFunnelType(order: IOrderDetailGetItem): 'individual' | 'party' | 'recruitment' {
  const orderTypeId = order.orderTypeId?.toUpperCase() ?? ''

  if (orderTypeId.includes('G')) {
    return 'recruitment'
  }

  if (order.eventId) {
    return 'party'
  }

  return 'individual'
}


onMounted(async () => {
  rootUrl.value = await GetRootUrl()
  const urlParams = new URLSearchParams(window.location.search)
  const aoeOrderIdToken = urlParams.get('aoeOrderIdToken')
  if (aoeOrderIdToken) {
    OrderDetailGet(aoeOrderIdToken)
      .then(async response => {
        if (response && response.item) {
          viewModel.value = response.item
          const orderCoupon = (response.item as IOrderDetailGetItem & {
            coupon?: string
            couponCode?: string
          }).couponCode || (response.item as IOrderDetailGetItem & {
            coupon?: string
            couponCode?: string
          }).coupon
          const uniqueProductCodes = Array.from(
            new Set(response.item.skus.map(sku => sku.productCode)),
          )
          GetFullProductImageDictionary(uniqueProductCodes).then(response => {
            if (!viewModel.value) return
            viewModel.value.skus.forEach(sku => {
              sku.imageUrl = response[sku.skuCode]
            })
          })

          const matomoItems = await buildMatomoItemsFromOrder(
            response.item.skus,
            response.item.orderTypeId,
            { coupon: orderCoupon },
          )

          const customerType = await getPurchaseCustomerType(response.item)

          const purchaseData: IPurchaseData = {
            transaction_id: response.item.aoeOrderId.toString(),
            value: response.item.total,
            tax: response.item.totalTaxAmountIncluded ?? 0,
            shipping: response.item.shippingCost ?? 0,
            currency: 'EUR',
            ...(orderCoupon ? { coupon: orderCoupon } : {}),
            customer_type: customerType,
            funnel_type: getPurchaseFunnelType(response.item),
            items: matomoItems,
          }
          trackPurchase(purchaseData)
        }
      })
      .catch(async error => {
        await handleError(error, t)
      })
  }
})

function continueShoppingClickedHandler(event: MouseEvent) {
  logger.debug('continueShoppingClickedHandler', event, rootUrl.value)

  window.location.href = rootUrl.value || '/'
}

</script>

<template>
  <div class="aoe-thank-you container">
    <template v-if="viewModel">
      <div class="thank-you-header">{{ t('thankYou.PageHeader') }}</div>
      <div class="thank-you-sub-header mt-3">
        <span class="thank-you-label" v-html="t('thankYou.SubHeader')"></span>
      </div>
      <div class="row mt-3 thank-you-section-header-row">
        <div class="col-12 thank-you-section-header-div order-detail-section-header">
          {{ t('thankYou.DetailsAboutOrder') }}
        </div>
      </div>
      <div class="order-info mt-3">
        <span class="thank-you-label">{{ t('thankYou.orderNumber') }}</span>
        <span class="thank-you-value"># {{ viewModel.aoeOrderId }}</span>
      </div>
      <div class="order-info mt-3">
        <span class="thank-you-label">{{ t('thankYou.PaymentAmount') }}:</span>
        <span class="thank-you-value"> {{ FormatCurrency(viewModel.total, null) }}</span>
      </div>
    <div class="row mt-3 thank-you-section-header-row">
      <div class="col-12 thank-you-section-header-div order-items-section-header">
        {{ t('thankYou.YourItems') }}
      </div>
    </div>
    <div class="row mt-3">
      <div class="col-12">
        <Grid
          ref="gridCartItems"
          :data-items="viewModel.skus"
          :resizable="true"
          :pageable="false"
          class="order-item-grid"
          :columns="columns">
          <template v-slot:orderItemGridImage="{ props }">
            {{ console.log(props) }}
            <td v-if="props.rowType !== 'groupHeader'" class="cart-item-grid-image">
              <img
                  :src="props.dataItem.imageUrl"
                  class="img-fluid prodImage"
                  :alt="t('cart.ProductImageAlt')"/>
            </td>
          </template>
          <template v-slot:orderItemGridPrice="{ props }">
            <td v-if="props.rowType !== 'groupHeader'" class="cart-item-grid-price">
              {{ FormatCurrency(props.dataItem.price ?? 0, null) }}
            </td>
          </template>
          <template v-slot:orderItemGridDiscount="{ props }">
            <td v-if="props.rowType !== 'groupHeader'" class="cart-item-grid-discount">
              {{ FormatCurrency(props.dataItem.totalDiscountAmount ?? 0, null) }}
            </td>
          </template>
          <template v-slot:orderItemGridSubtotal="{ props }">
            <td v-if="props.rowType !== 'groupHeader'" class="cart-item-grid-subtotal">
              {{ FormatCurrency((props.dataItem.price * props.dataItem.quantity) - props.dataItem.totalDiscountAmount, null) }}
            </td>
          </template>
        </Grid>
      </div>
    </div>
    <div class="summary-item row mt-3">
      <div class="col-8 col-md-4 col-lg-3 summary-label">{{ t('thankYou.shippingCost') }}</div>
      <div class="col-4 col-md-2 text-end summary-value">{{ FormatCurrency(viewModel.shippingCost ?? 0, null) }}</div>
    </div>
    <div class="summary-item row">
      <div class="col-8 col-md-4 col-lg-3 summary-label">{{ t('thankYou.total') }}</div>
      <div class="col-4 col-md-2 text-end summary-value">{{ FormatCurrency(viewModel.total, null) }}</div>
    </div>
    <!-- <div class="summary-item row">
      <div class="col-2 offset-8 summary-label">{{ t('thankYou.totalTaxAmountInclude') }}</div>
      <div class="col-2 text-end summary-value">{{ FormatCurrency(viewModel.totalTaxAmountIncluded ?? 0, null) }}</div>
    </div> -->
    </template>
  </div>
  <div class="row pt-5">
    <div class="col">
      <KButton
        :rounded="'large'"
        :theme-color="'checkoutContinue'"
        @click="continueShoppingClickedHandler($event)"
        class="baseBtn primaryBtn continue-shopping-button"
      >{{ t('cart.ContinueShopping') }}
      </KButton>
    </div>
  </div>
</template>

<style lang="scss">

.order-item-grid .k-grid-content
{
  overflow-y: hidden;
}

.order-item-grid .k-grid-header
{
  padding: inherit !important;
  padding-inline-end: inherit;
}

.aoe-thank-you {
  .prodImage {
    width: 100px;
    height: 100px;
  }

  .summary-item {
    justify-content: end;
  }
}

.thank-you-section-header-div
{
  @include typography-body-large;
  @include font-weight-semibold;
  padding: $spacing-2-5;
  background-color: var(--section-header-color);
  margin-left: $spacing-2-5;
}

.thank-you-value
{
  margin-left: 10px;
}

.thank-you-header
{
  @include typography-h3;
  @include font-weight-semibold;
}

.order-item-grid-qty-header .k-column-title
{
  margin-left: auto;
  margin-right: auto;
}

</style>
